<template>
    <v-dialog v-model="dialog" max-width="500" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ editMode ? $t('common.edit') : $t('common.add') }} {{ $t(`common.injury`) }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12">
                        <kimsa-date-picker
                            :label="$t('common.date_injury')"
                            solo flat force-value
                            dense outlined
                            color="secondary"
                            @change="date = $event"
                            :value="date"
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-combobox
                            :label="$t('common.injury')"
                            :placeholder="$t('common.select_placeholder')"
                            outlined dense
                            :items="typeOptions"
                            @change="type = $event;"
                            :value="type"
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-combobox
                            :label="$t('common.zone')"
                            :placeholder="$t('common.select_placeholder')"
                            outlined dense
                            :items="zoneOptions"
                            @change="zone = $event;"
                            :value="zone"
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-combobox
                            :label="$t('common.condition')"
                            :placeholder="$t('common.select_placeholder')"
                            outlined dense
                            :items="conditionOptions"
                            @change="condition = $event;"
                            :value="condition"
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-combobox
                            :label="$t('common.was_it')"
                            :placeholder="$t('common.select_placeholder')"
                            outlined dense
                            :items="wasItOptions"
                            @change="wasIt = $event;"
                            :value="wasIt"
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-date-picker
                            :label="$t('common.discharge_date')"
                            solo flat force-value
                            dense outlined
                            color="secondary"
                            @change="dischargeDate = $event"
                            :value="dischargeDate"
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-textarea-field
                            outlined no-elevation
                            :label="$t('common.comment')"
                            @change="comment = $event"
                            :value="comment" force-value
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn black--text no-radius"
                       :disabled="disabled"
                       @click="save" :loading="loading">
                    {{ $t('common.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>

    </v-dialog>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";
import {db, Timestamp} from "@/utils/Firebase";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";

export default {
    name: "InjuryFormDialog",
    components: {KimsaTextareaField, KimsaCombobox, KimsaDatePicker},
    data() {
        return {
            dialog: false,
            loading: false,
            injury: null,
            editMode: false,
            date: null,
            type: null,
            zone: null,
            condition: null,
            wasIt: null,
            dischargeDate: null,
            comment: '',
        }
    },
    computed: {
        ...mapState(['dialogActive', 'config']),
        disabled(vm = this) {
            return !vm.date || !vm.type || !vm.zone || !vm.condition || !vm.wasIt
        },
        typeOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.type
        },
        zoneOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.zone
        },
        conditionOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.condition
        },
        wasItOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.wasIt
        },
    },
    async mounted() {
        const vm = this
        if (!vm.config) await vm.bindConfig()
    },
    methods: {
        ...mapActions(['bindConfig']),
        ...mapMutations(['setDialogActive']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.injury = null
            vm.editMode = false
            vm.date = null
            vm.type = null
            vm.zone = null
            vm.condition = null
            vm.wasIt = null
            vm.dischargeDate = null
        },
        loadInjury(injury) {
            const vm = this
            vm.injury = injury
            vm.editMode = true
            vm.date = injury.date
            vm.type = injury.type
            vm.zone = injury.zone
            vm.condition = injury.condition
            vm.wasIt = injury.wasIt
            vm.dischargeDate = injury.dischargeDate
            vm.show()
        },
        async save() {
            const vm = this
            vm.loading = true

            let hasInjury = !vm.dischargeDate || (vm.dischargeDate && vm.$moment(vm.dischargeDate).isAfter(vm.$moment()))

            if (vm.editMode) {
                await db.collection('injuries').doc(vm.injury.id).update({
                    date: vm.date,
                    type: vm.type,
                    zone: vm.zone,
                    condition: vm.condition,
                    wasIt: vm.wasIt,
                    dischargeDate: vm.dischargeDate,
                    comment: vm.comment
                })
                vm.toast(vm.$t('common.injury_edited'))
            } else {
                await db.collection('injuries').add({
                    studentId: vm.currentUser.uid,
                    date: vm.date,
                    type: vm.type,
                    zone: vm.zone,
                    condition: vm.condition,
                    wasIt: vm.wasIt,
                    dischargeDate: vm.dischargeDate,
                    comment: vm.comment
                })
                await vm.sendMessageToCoach()
                vm.toast(vm.$t('common.injury_added'))
            }

            await db.collection('users').doc(vm.currentUser.uid).update({
                hasInjury: hasInjury,
            })

            vm.loading = false
            vm.hide()
            vm.$emit('success')
        },
        async sendMessageToCoach() {
            const vm = this

            let message = `${vm.$t('common.date_injury')}: ${vm.$moment(vm.date).format('DD-MM-YYYY')} \n${vm.$t('common.injury')}: ${vm.type} \n${vm.$t('common.zone')}: ${vm.zone} \n${vm.$t('common.condition')}: ${vm.condition} \n${vm.$t('common.was_it')}: ${vm.wasIt} \n${vm.$t('common.discharge_date')}: ${vm.$moment(vm.date).format('DD-MM-YYYY')} \n${vm.$t('common.comment')}: ${vm.comment}`

            let studentId = vm.currentUser?.uid || null
            let coachId = vm.account?.studentSubscription?.coachId || null

            let chatRef = await db.collection('privateChats')
                .where('type', '==', 'coach_student')
                .where('participants', 'in', [[coachId, studentId]])
                .get()

            let chatId = null
            if (chatRef.docs.length > 0) {
                chatId = chatRef.docs[0].id
            } else {
                let newChat = await db.collection('privateChats').add({
                    type: 'coach_student',
                    participants: [coachId, studentId]
                })
                chatId = newChat.id
            }

            let payload = {
                message: message,
                from: studentId,
                read: false,
                timestamp: Timestamp.now(),
                type: 'text'
            }

            await db.collection('privateChats').doc(chatId).collection('messages').add(payload)
            await db.collection('privateChats').doc(chatId).update({lastMessage: payload})
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
    }
}
</script>

<style scoped>

.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

</style>